import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Share2, MessageCircle, Mail, Smartphone, Link2, Loader2, Check } from "lucide-react";

const digits = (s) => (s || "").replace(/[^\d]/g, "");

export default function ShareMenu({ rxId, patient, doctor }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const getLink = async () => {
    if (link) return link;
    setLoading(true);
    try {
      const r = await api.post(`/prescriptions/${rxId}/share`);
      setLink(r.data.link);
      return r.data.link;
    } catch {
      toast.error("Could not create share link");
      return null;
    } finally { setLoading(false); }
  };

  const clinic = doctor?.clinic_name || "MDvia Care";
  const docName = doctor?.full_name || doctor?.name || "";
  const docContact = [doctor?.contact_phone, doctor?.contact_email].filter(Boolean).join(" · ");
  const buildMsg = (l) =>
    `Dear ${patient?.name || "Patient"}, thank you for visiting ${clinic}. Here is your prescription: ${l}` +
    (docName ? `\n\n— ${docName}` : "") + (docContact ? `\n${docContact}` : "");

  const shareWhatsApp = async () => {
    const l = await getLink(); if (!l) return;
    const to = digits(patient?.whatsapp || patient?.phone);
    window.open(`https://wa.me/${to}?text=${encodeURIComponent(buildMsg(l))}`, "_blank");
    setOpen(false);
  };
  const shareSMS = async () => {
    const l = await getLink(); if (!l) return;
    window.location.href = `sms:${patient?.phone || ""}?&body=${encodeURIComponent(buildMsg(l))}`;
    setOpen(false);
  };
  const shareEmail = async () => {
    const l = await getLink(); if (!l) return;
    const subject = `Your Prescription from ${clinic}`;
    window.location.href = `mailto:${patient?.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildMsg(l))}`;
    setOpen(false);
  };
  const copyLink = async () => {
    const l = await getLink(); if (!l) return;
    try {
      await navigator.clipboard.writeText(l);
      setCopied(true); toast.success("Prescription link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const phone = patient?.phone;
  const wa = patient?.whatsapp || patient?.phone;
  const email = patient?.email;

  return (
    <div className="relative">
      <button data-testid="btn-share-prescription" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#F59E0B] text-white text-sm font-semibold hover:bg-amber-500">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share to Patient
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-60 rounded-2xl border border-slate-200 bg-white shadow-xl z-40 overflow-hidden p-1.5" data-testid="share-menu">
            <MenuItem testId="share-whatsapp" icon={MessageCircle} label="WhatsApp" sub={wa || "no number on file"} disabled={!wa} onClick={shareWhatsApp} />
            <MenuItem testId="share-sms" icon={Smartphone} label="SMS" sub={phone || "no number on file"} disabled={!phone} onClick={shareSMS} />
            <MenuItem testId="share-email" icon={Mail} label="Email" sub={email || "no email on file"} disabled={!email} onClick={shareEmail} />
            <div className="my-1 border-t border-slate-100" />
            <MenuItem testId="share-copy" icon={copied ? Check : Link2} label={copied ? "Copied!" : "Copy link"} onClick={copyLink} />
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, sub, onClick, disabled, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#EBF3FB] text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
      <span className="w-8 h-8 rounded-lg bg-[#EBF3FB] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#1E5AA8]" /></span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800">{label}</span>
        {sub && <span className="block text-[11px] text-slate-400 truncate" data-notranslate>{sub}</span>}
      </span>
    </button>
  );
}
